import { useState, useEffect, useMemo, useRef } from 'react';
import * as d3 from 'd3';
import {
  ArrowPathIcon,
  MagnifyingGlassIcon,
  DocumentArrowDownIcon,
  CalendarDaysIcon,
  ClockIcon,
  DocumentTextIcon,
  SparklesIcon,
  BoltIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
} from '@heroicons/react/24/outline';
import { PieChart, Pie, Cell, Tooltip as RTooltip, ResponsiveContainer } from 'recharts';
import { useAuth } from '../context/AuthContext';
import { useStation } from '../context/StationContext';
import {
  useMemorySummaryDaily,
  useMemorySummaryLatest,
  useMemorySummaryPeriodic,
  type DailySummaryItem,
  type PeriodicSummaryItem,
} from '../hooks/useMemorySummary';
import MarkdownText from '../components/shared/MarkdownText';
import DailySummaryChart from '../components/charts/DailySummaryChart';
import PageHeader from '../components/layout/PageHeader';
import EmptyState from '../components/shared/EmptyState';
import StatusBadge from '../components/shared/StatusBadge';
import { toast } from '../components/shared/Toast';
import { useTranslation } from 'react-i18next';

/* โ”€โ”€ types โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€ */

type GraphNode = {
  id: string;
  type: 'daily' | 'periodic' | 'topic' | 'station';
  label: string;
  radius: number;
  color: string;
  data?: DailySummaryItem | PeriodicSummaryItem;
  x?: number;
  y?: number;
  fx?: number | null;
  fy?: number | null;
};

type GraphLink = d3.SimulationLinkDatum<GraphNode> & {
  type: 'temporal' | 'topic' | 'org';
};

/* โ”€โ”€ helpers โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€ */

function getCSSVarRGB(varName: string): string {
  const root = document.documentElement;
  const value = getComputedStyle(root).getPropertyValue(varName).trim();
  return value || '100,100,100';
}

function extractKeywords(md: string, topN = 5): string[] {
  const stopwords = new Set([
    'the', 'and', 'to', 'of', 'a', 'in', 'is', 'it', 'for', 'with', 'as', 'on', 'by', 'at',
    'from', 'or', 'be', 'are', 'have', 'has', 'had', 'do', 'does', 'did', 'can', 'will',
    'would', 'could', 'should', 'may', 'might', 'must', 'shall', 'that', 'this', 'these',
    'those', 'which', 'who', 'what', 'when', 'where', 'why', 'how',
  ]);

  const text = md.toLowerCase().replace(/[#*\->\[\]()]/g, ' ');
  const words = text.split(/\s+/).filter((w) => w.length > 2 && !stopwords.has(w));

  const freq: Record<string, number> = {};
  words.forEach((w) => {
    freq[w] = (freq[w] || 0) + 1;
  });

  return Object.entries(freq)
    .sort((a, b) => b[1] - a[1])
    .slice(0, topN)
    .map(([w]) => w);
}

function buildGraphData(
  dailyItems: DailySummaryItem[],
  periodicItems: PeriodicSummaryItem[],
  filterOrg: string
): { nodes: GraphNode[]; links: GraphLink[] } {
  const nodes: GraphNode[] = [];
  const links: GraphLink[] = [];
  const nodeMap = new Map<string, GraphNode>();
  const topicMap = new Map<string, Set<string>>();

  const filteredDaily = filterOrg ? dailyItems.filter((d) => d.org_id === filterOrg) : dailyItems;

  filteredDaily.forEach((daily, i) => {
    const nodeId = `daily-${daily.summary_date}-${daily.org_id}`;
    const rgb = getCSSVarRGB('--color-app-accent');
    const node: GraphNode = {
      id: nodeId,
      type: 'daily',
      label: daily.summary_date,
      radius: Math.min(28, Math.max(8, (daily.source_turn_count || 5) / 2)),
      color: `rgb(${rgb})`,
      data: daily,
    };
    nodes.push(node);
    nodeMap.set(nodeId, node);

    if (i > 0 && filteredDaily[i - 1].org_id === daily.org_id) {
      const prevId = `daily-${filteredDaily[i - 1].summary_date}-${filteredDaily[i - 1].org_id}`;
      links.push({
        source: prevId,
        target: nodeId,
        type: 'temporal',
      });
    }

    extractKeywords(daily.summary_md, 5).forEach((kw) => {
      if (!topicMap.has(kw)) topicMap.set(kw, new Set());
      topicMap.get(kw)!.add(nodeId);
    });
  });

  topicMap.forEach((nodeIds, keyword) => {
    if (nodeIds.size >= 2) {
      const topicId = `topic-${keyword}`;
      const node: GraphNode = {
        id: topicId,
        type: 'topic',
        label: keyword,
        radius: 6,
        color: `rgb(150,140,160)`,
      };
      nodes.push(node);
      nodeMap.set(topicId, node);

      nodeIds.forEach((nodeId) => {
        links.push({
          source: nodeId,
          target: topicId,
          type: 'topic',
        });
      });
    }
  });

  const stations = new Set(filteredDaily.map((d) => d.station_id || 'All').filter(Boolean));
  stations.forEach((station) => {
    const stationId = `station-${station}`;
    const node: GraphNode = {
      id: stationId,
      type: 'station',
      label: station,
      radius: 10,
      color: `rgb(100,200,100)`,
    };
    nodes.push(node);
    nodeMap.set(stationId, node);

    filteredDaily.forEach((daily) => {
      if ((daily.station_id || 'All') === station) {
        links.push({
          source: `daily-${daily.summary_date}-${daily.org_id}`,
          target: stationId,
          type: 'org',
        });
      }
    });
  });

  return { nodes, links };
}

function downloadBlob(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function exportJSON(data: unknown, filename: string) {
  downloadBlob(JSON.stringify(data, null, 2), filename, 'application/json');
}

function exportCSV(rows: string[][], filename: string) {
  const content = rows
    .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  downloadBlob(content, filename, 'text/csv');
}

function formatRelativeTime(iso: string | undefined | null): string {
  if (!iso) return 'โ€”';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return iso;
  const now = Date.now();
  const diffSec = Math.floor((now - date.getTime()) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} hr ago`;
  if (diffSec < 86400 * 7) return `${Math.floor(diffSec / 86400)} d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/* โ”€โ”€ Stat chip (compact KPI) โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€ */

function StatChip({
  icon: Icon,
  label,
  value,
  hint,
  delta,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | number;
  hint?: string;
  delta?: { value: number; positive: boolean } | null;
}) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-app-border bg-app-surface px-3 py-2 min-w-0">
      <Icon className="w-4 h-4 text-app-accent shrink-0" />
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-app-text-dim font-semibold truncate">{label}</p>
        <p className="text-sm font-semibold text-app-text tabular-nums truncate" title={String(value)}>
          {value}
        </p>
        {hint && <p className="text-[10px] text-app-text-dim truncate">{hint}</p>}
      </div>
      {delta && (
        <span
          className={`ml-auto inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full shrink-0 ${
            delta.positive ? 'bg-app-success/15 text-app-success' : 'bg-app-error/15 text-app-error'
          }`}
        >
          {delta.positive ? <ArrowTrendingUpIcon className="w-3 h-3" /> : <ArrowTrendingDownIcon className="w-3 h-3" />}
          {delta.positive ? '+' : ''}
          {delta.value}%
        </span>
      )}
    </div>
  );
}

function Panel({
  title,
  icon: Icon,
  children,
  className = '',
  bodyClassName = '',
}: {
  title: string;
  icon?: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section
      className={`flex flex-col min-h-0 rounded-xl border border-app-border bg-app-surface shadow-sm overflow-hidden ${className}`}
    >
      <header className="shrink-0 flex items-center gap-2 px-3 py-2 border-b border-app-border/60 bg-app-muted/30">
        {Icon && <Icon className="w-4 h-4 text-app-accent shrink-0" />}
        <h2 className="text-xs font-semibold text-app-text truncate">{title}</h2>
      </header>
      <div className={`flex-1 min-h-0 overflow-y-auto p-3 ${bodyClassName}`}>{children}</div>
    </section>
  );
}

/* โ”€โ”€ Activity heatmap โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€ */

function ActivityHeatmap({ items }: { items: DailySummaryItem[] }) {
  const data = useMemo(() => {
    const map = new Map<string, number>();
    items.forEach((it) => {
      if (it.summary_date) map.set(it.summary_date, it.source_turn_count);
    });
    const days: { date: string; count: number }[] = [];
    const today = new Date();
    for (let i = 29; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      days.push({ date: key, count: map.get(key) ?? 0 });
    }
    return days;
  }, [items]);

  const max = Math.max(1, ...data.map((d) => d.count));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-wider text-app-text-dim font-semibold">
          30-Day Activity
        </p>
        <div className="flex items-center gap-1 text-[10px] text-app-text-dim">
          <span>less</span>
          {[0.15, 0.35, 0.6, 0.85].map((o) => (
            <span
              key={o}
              className="w-2.5 h-2.5 rounded-sm"
              style={{ backgroundColor: `rgb(var(--color-app-accent) / ${o})` }}
            />
          ))}
          <span>more</span>
        </div>
      </div>

      <div className="grid grid-cols-[repeat(30,minmax(0,1fr))] gap-1">
        {data.map((d) => {
          const intensity = d.count === 0 ? 0 : 0.15 + (d.count / max) * 0.7;
          return (
            <div key={d.date} className="relative group/cell aspect-square">
              <div
                className="w-full h-full rounded-sm border border-app-border/40"
                style={{
                  backgroundColor:
                    intensity === 0
                      ? 'rgb(var(--color-app-muted))'
                      : `rgb(var(--color-app-accent) / ${intensity})`,
                }}
              />
              <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 rounded-md bg-app-text text-[10px] text-app-surface whitespace-nowrap opacity-0 group-hover/cell:opacity-100 transition-opacity z-10 shadow-md">
                {d.date} ยท {d.count} turns
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* โ”€โ”€ Period donut โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€ */

const PERIOD_PALETTE = [
  'rgb(var(--color-app-accent))',
  'rgb(var(--color-app-success))',
  'rgb(var(--color-app-warning))',
  'rgb(var(--color-app-master))',
];

function PeriodDonut({ items }: { items: PeriodicSummaryItem[] }) {
  const data = useMemo(() => {
    const counts: Record<string, number> = { week: 0, month: 0, quarter: 0, year: 0 };
    items.forEach((it) => {
      counts[it.period_type] = (counts[it.period_type] || 0) + 1;
    });
    return Object.entries(counts)
      .filter(([, v]) => v > 0)
      .map(([name, value]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value }));
  }, [items]);

  if (!data.length) return null;
  const total = data.reduce((s, e) => s + e.value, 0);

  return (
    <div className="flex items-center gap-3">
      <div className="w-20 h-20 relative">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="value" innerRadius={22} outerRadius={36} paddingAngle={2}>
              {data.map((_, i) => (
                <Cell key={i} fill={PERIOD_PALETTE[i % PERIOD_PALETTE.length]} stroke="none" />
              ))}
            </Pie>
            <RTooltip
              contentStyle={{
                background: 'rgb(var(--color-app-surface))',
                border: '1px solid rgb(var(--color-app-border))',
                borderRadius: 8,
                fontSize: 11,
                padding: '4px 8px',
              }}
              formatter={(value) => [`${Number(value || 0)} records`, '']}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="text-xs font-semibold text-app-text tabular-nums">{total}</span>
        </div>
      </div>
      <div className="space-y-1">
        {data.map((d, i) => (
          <div key={d.name} className="flex items-center gap-2 text-[11px]">
            <span
              className="w-2 h-2 rounded-sm shrink-0"
              style={{ backgroundColor: PERIOD_PALETTE[i % PERIOD_PALETTE.length] }}
            />
            <span className="text-app-text-sec">{d.name}</span>
            <span className="text-app-text-dim tabular-nums">ยท</span>
            <span className="text-app-text font-medium tabular-nums">{d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* โ”€โ”€ Skeleton card โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€ */

function SkeletonCard() {
  return (
    <div className="bg-app-surface border border-app-border rounded-xl p-5 animate-pulse">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="space-y-2 flex-1">
          <div className="h-3 w-32 bg-app-muted rounded" />
          <div className="h-2.5 w-48 bg-app-muted/70 rounded" />
        </div>
        <div className="h-6 w-20 bg-app-muted rounded-md" />
      </div>
      <div className="space-y-2">
        <div className="h-2.5 w-full bg-app-muted/60 rounded" />
        <div className="h-2.5 w-11/12 bg-app-muted/60 rounded" />
        <div className="h-2.5 w-4/5 bg-app-muted/60 rounded" />
      </div>
    </div>
  );
}

/* Memory Graph (D3) */

function MemoryGraph({
  dailyItems,
  periodicItems,
  filterOrg,
  onNodeClick,
}: {
  dailyItems: DailySummaryItem[];
  periodicItems: PeriodicSummaryItem[];
  filterOrg: string;
  onNodeClick?: (node: GraphNode) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const simulationRef = useRef<d3.Simulation<GraphNode, GraphLink> | null>(null);
  const [graphSize, setGraphSize] = useState({ width: 800, height: 400 });

  const { nodes, links } = useMemo(
    () => buildGraphData(dailyItems, periodicItems, filterOrg),
    [dailyItems, periodicItems, filterOrg]
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const update = () => {
      const width = Math.max(200, el.clientWidth);
      const height = Math.max(240, el.clientHeight);
      setGraphSize({ width, height });
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!svgRef.current || nodes.length === 0) return;

    const { width, height } = graphSize;
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const simulation = d3
      .forceSimulation(nodes)
      .force('link', d3.forceLink(links).id((d: any) => d.id).distance(60))
      .force('charge', d3.forceManyBody().strength(-120))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide((d: any) => d.radius + 4));

    simulationRef.current = simulation;

    const g = svg.append('g');
    const accentRGB = getCSSVarRGB('--color-app-accent');
    const linkElements = g
      .selectAll('line')
      .data(links)
      .enter()
      .append('line')
      .attr('stroke', (d: any) =>
        d.type === 'temporal' ? `rgba(${accentRGB},0.6)` : d.type === 'topic' ? 'rgba(150,140,160,0.3)' : 'rgba(100,200,100,0.3)'
      )
      .attr('stroke-width', (d: any) => (d.type === 'temporal' ? 1.5 : 1))
      .attr('stroke-dasharray', (d: any) => (d.type === 'topic' ? '4,2' : d.type === 'org' ? '2,2' : 'none'));

    const nodeElements = g
      .selectAll('circle')
      .data(nodes)
      .enter()
      .append('circle')
      .attr('r', (d: any) => d.radius)
      .attr('fill', (d: any) => d.color)
      .attr('opacity', 0.8)
      .attr('stroke', 'currentColor')
      .attr('stroke-width', 2)
      .style('cursor', (d: any) => (d.type === 'daily' ? 'pointer' : 'default'))
      .on('click', (e, d: any) => {
        if (d.type === 'daily' && onNodeClick) onNodeClick(d);
      });

    const labels = g
      .selectAll('text')
      .data(nodes)
      .enter()
      .append('text')
      .attr('font-size', '10px')
      .attr('text-anchor', 'middle')
      .attr('dy', '0.3em')
      .attr('fill', 'currentColor')
      .attr('pointer-events', 'none')
      .text((d: any) => (d.type === 'daily' ? d.label.slice(-5) : d.label.slice(0, 8)));

    const zoom = d3.zoom().on('zoom', (e: any) => {
      g.attr('transform', e.transform);
    });

    svg.call(zoom as any);

    svg.on('dblclick.zoom', null);

    const drag = d3.drag().on('start', (e: any) => {
      const d = e.subject as GraphNode;
      if (!e.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    });

    nodeElements
      .call(drag as any)
      .on('drag', (e: any) => {
        const d = e.subject as GraphNode;
        d.fx = e.x;
        d.fy = e.y;
      })
      .on('end', (e: any) => {
        const d = e.subject as GraphNode;
        if (!e.active) simulation.alphaTarget(0);
        d.fx = null;
        d.fy = null;
      });

    simulation.on('tick', () => {
      linkElements
        .attr('x1', (d: any) => d.source.x)
        .attr('y1', (d: any) => d.source.y)
        .attr('x2', (d: any) => d.target.x)
        .attr('y2', (d: any) => d.target.y);

      nodeElements.attr('cx', (d: any) => d.x).attr('cy', (d: any) => d.y);

      labels.attr('x', (d: any) => d.x).attr('y', (d: any) => d.y);
    });

    return () => {
      simulation.stop();
    };
  }, [nodes, links, onNodeClick, graphSize]);

  return (
    <div ref={containerRef} className="h-full w-full min-h-0">
      <svg
        ref={svgRef}
        width={graphSize.width}
        height={graphSize.height}
        className="block max-h-full max-w-full"
      />
    </div>
  );
}

/* โ”€โ”€ main page โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€ */

const REFRESH_OPTIONS = [
  { label: '30s', ms: 30_000 },
  { label: '1m', ms: 60_000 },
  { label: '5m', ms: 300_000 },
];

const RECENT_LIST_LIMIT = 6;

export default function MemorySummaryPage() {
  const { t } = useTranslation();
  const { userOrgName } = useAuth();
  const { stations, activeStationId, setActiveStationId, loading: stationsLoading } = useStation();
  const [searchQuery, setSearchQuery] = useState('');
  const [periodType, setPeriodType] = useState<'' | 'week' | 'month' | 'quarter' | 'year'>('');
  const pageSize = 30;

  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshIntervalMs, setRefreshIntervalMs] = useState(30_000);
  const [selectedGraphNode, setSelectedGraphNode] = useState<GraphNode | null>(null);

  const stationId = activeStationId;
  const activeStation = stations.find((s) => s.id === activeStationId);
  const orgId = String(activeStation?.org_name ?? userOrgName ?? activeStationId ?? '').trim();

  const latest = useMemorySummaryLatest(orgId, stationId);
  const daily = useMemorySummaryDaily(orgId, stationId, 30, 0, pageSize);
  const periodic = useMemorySummaryPeriodic(periodType, orgId, stationId, 0, pageSize);

  /* auto-refresh */
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => {
      void latest.refresh();
      void daily.refresh();
      void periodic.refresh();
    }, refreshIntervalMs);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, refreshIntervalMs, latest.refresh, daily.refresh, periodic.refresh]);

  const handleManualRefresh = () => {
    void latest.refresh();
    void daily.refresh();
    void periodic.refresh();
  };

  /* โ”€โ”€ search filter (client-side) โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€ */
  const filteredDailyItems = useMemo(() => {
    if (!searchQuery.trim()) return daily.data?.items ?? [];
    const q = searchQuery.toLowerCase();
    return (daily.data?.items ?? []).filter(
      (i) =>
        i.summary_md.toLowerCase().includes(q) ||
        (i.summary_date && i.summary_date.toLowerCase().includes(q)) ||
        (i.station_id && i.station_id.toLowerCase().includes(q)),
    );
  }, [daily.data, searchQuery]);

  const filteredPeriodicItems = useMemo(() => {
    if (!searchQuery.trim()) return periodic.data?.items ?? [];
    const q = searchQuery.toLowerCase();
    return (periodic.data?.items ?? []).filter(
      (i) =>
        i.summary_md.toLowerCase().includes(q) ||
        (i.period_type && i.period_type.toLowerCase().includes(q)) ||
        (i.station_id && i.station_id.toLowerCase().includes(q)),
    );
  }, [periodic.data, searchQuery]);

  const recentDailyItems = useMemo(
    () =>
      [...filteredDailyItems]
        .sort((a, b) => b.summary_date.localeCompare(a.summary_date))
        .slice(0, RECENT_LIST_LIMIT),
    [filteredDailyItems],
  );

  const recentPeriodicItems = useMemo(
    () =>
      [...filteredPeriodicItems]
        .sort((a, b) => b.period_end_date.localeCompare(a.period_end_date))
        .slice(0, RECENT_LIST_LIMIT),
    [filteredPeriodicItems],
  );

  /* โ”€โ”€ export helpers (with toast) โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€ */
  const exportDailyCSV = () => {
    if (!daily.data) return;
    exportCSV(
      [
        ['summary_date', 'org_id', 'station_id', 'source_turn_count', 'summary_md'],
        ...daily.data.items.map((i) => [
          i.summary_date,
          i.org_id ?? '',
          i.station_id ?? '',
          String(i.source_turn_count),
          i.summary_md,
        ]),
      ],
      `memory_daily_${stationId || 'all'}.csv`,
    );
    toast.success(`Exported ${daily.data.items.length} daily records`);
  };

  const handleExportJSON = (kind: 'daily' | 'periodic') => {
    if (kind === 'daily' && daily.data) {
      exportJSON(daily.data.items, `memory_daily_${stationId || 'all'}.json`);
      toast.success('Daily summaries exported as JSON');
    } else if (kind === 'periodic' && periodic.data) {
      exportJSON(periodic.data.items, `memory_periodic_${stationId || 'all'}.json`);
      toast.success('Periodic summaries exported as JSON');
    }
  };

  /* โ”€โ”€ derived stats โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€โ”€ */
  const dailyCount = daily.data?.total ?? 0;
  const periodicCount = periodic.data?.total ?? 0;
  const latestDailyDate = latest.data?.daily?.summary_date ?? 'โ€”';
  const latestUpdatedAt = latest.data?.daily?.updated_at ?? latest.data?.daily?.created_at;

  const sortedDailyAsc = useMemo(
    () => [...(daily.data?.items ?? [])].sort((a, b) => a.summary_date.localeCompare(b.summary_date)),
    [daily.data],
  );

  const totalTurns30d = useMemo(
    () => sortedDailyAsc.reduce((sum, it) => sum + (it.source_turn_count || 0), 0),
    [sortedDailyAsc],
  );

  const dailyDelta = useMemo(() => {
    if (sortedDailyAsc.length < 14) return null;
    const last7 = sortedDailyAsc.slice(-7).reduce((s, i) => s + i.source_turn_count, 0);
    const prev7 = sortedDailyAsc.slice(-14, -7).reduce((s, i) => s + i.source_turn_count, 0);
    if (prev7 === 0) return null;
    const pct = Math.round(((last7 - prev7) / prev7) * 100);
    return { value: Math.abs(pct), positive: pct >= 0 };
  }, [sortedDailyAsc]);

  const periodicBreakdownHint = useMemo(() => {
    if (!periodic.data?.items.length) return '';
    const counts: Record<string, number> = { week: 0, month: 0, quarter: 0, year: 0 };
    periodic.data.items.forEach((it) => {
      counts[it.period_type] = (counts[it.period_type] || 0) + 1;
    });
    return Object.entries(counts)
      .filter(([, v]) => v > 0)
      .map(([k, v]) => `${k[0].toUpperCase()}${v}`)
      .join(' ยท ');
  }, [periodic.data]);

  const activeStationLabel = activeStation?.name || activeStationId || 'All stations';

  const hasAnyError = latest.error || daily.error || periodic.error;

  return (
    <div className="mx-auto flex h-[calc(100vh-140px)] min-h-[520px] w-full max-w-screen-2xl flex-col gap-3 overflow-hidden">
      <PageHeader
        title={t('memory.pageTitle')}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <span>{t('memory.pageDescription')}</span>
            <StatusBadge variant="info" dot>
              {activeStationLabel}
            </StatusBadge>
          </span>
        }
      >
        <button
          type="button"
          onClick={exportDailyCSV}
          disabled={!daily.data?.items.length}
          className="inline-flex items-center gap-1.5 rounded-lg border border-app-border bg-app-surface px-2.5 py-1.5 text-xs hover:border-app-accent/40 hover:text-app-accent disabled:opacity-40"
        >
          <DocumentArrowDownIcon className="w-3.5 h-3.5" />
          CSV
        </button>
        <button
          type="button"
          onClick={() => handleExportJSON('daily')}
          disabled={!daily.data?.items.length}
          className="inline-flex items-center gap-1.5 rounded-lg border border-app-border bg-app-surface px-2.5 py-1.5 text-xs hover:border-app-accent/40 hover:text-app-accent disabled:opacity-40"
        >
          JSON
        </button>
      </PageHeader>

      <div className="shrink-0 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <StatChip
          icon={DocumentTextIcon}
          label="Latest"
          value={latestDailyDate}
          hint={latestUpdatedAt ? formatRelativeTime(latestUpdatedAt) : 'โ€”'}
        />
        <StatChip
          icon={CalendarDaysIcon}
          label="Daily"
          value={dailyCount.toLocaleString()}
          hint="30d window"
          delta={dailyDelta}
        />
        <StatChip
          icon={ClockIcon}
          label="Periodic"
          value={periodicCount.toLocaleString()}
          hint={periodicBreakdownHint || 'โ€”'}
        />
        <StatChip
          icon={BoltIcon}
          label="Turns (30d)"
          value={totalTurns30d.toLocaleString()}
          hint="source_turn_count"
        />
      </div>

      <div className="shrink-0 rounded-xl border border-app-border bg-app-surface/85 p-2.5 shadow-sm backdrop-blur-md">
        <div className="flex flex-wrap gap-2 items-center">
          <select
            value={activeStationId}
            onChange={(e) => setActiveStationId(e.target.value)}
            disabled={stationsLoading}
            className="rounded-lg border border-app-border bg-app-surface px-3 py-2 text-sm focus:ring-2 focus:ring-app-accent/40 focus:border-app-accent disabled:opacity-50 transition-all"
          >
            <option value="">All stations</option>
            {stations.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name || s.id}
              </option>
            ))}
          </select>

          <select
            value={periodType}
            onChange={(e) =>
              setPeriodType((e.target.value as '' | 'week' | 'month' | 'quarter' | 'year') || '')
            }
            className="rounded-lg border border-app-border bg-app-surface px-3 py-2 text-sm focus:ring-2 focus:ring-app-accent/40 focus:border-app-accent transition-all"
          >
            <option value="">All periods</option>
            <option value="week">Week</option>
            <option value="month">Month</option>
            <option value="quarter">Quarter</option>
            <option value="year">Year</option>
          </select>

          <div className="flex-1 min-w-[10rem]">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-app-text-dim" />
              <input
                type="text"
                placeholder="Search summaries..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-app-border bg-app-surface text-sm text-app-text placeholder:text-app-text-dim focus:outline-none focus:ring-2 focus:ring-app-accent/40 focus:border-app-accent transition-all"
              />
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={handleManualRefresh}
              disabled={latest.loading || daily.loading || periodic.loading}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-app-text border border-app-border hover:bg-app-muted hover:text-app-accent disabled:opacity-50 transition-colors"
              aria-label="Refresh"
            >
              <ArrowPathIcon
                className={`w-4 h-4 ${
                  latest.loading || daily.loading || periodic.loading ? 'animate-spin' : ''
                }`}
              />
              Refresh
            </button>
            {autoRefresh && (
              <select
                value={refreshIntervalMs}
                onChange={(e) => setRefreshIntervalMs(Number(e.target.value))}
                className="rounded-lg border border-app-border bg-app-surface px-2 py-2 text-sm focus:ring-2 focus:ring-app-accent/40"
              >
                {REFRESH_OPTIONS.map((o) => (
                  <option key={o.ms} value={o.ms}>
                    {o.label}
                  </option>
                ))}
              </select>
            )}
            <button
              type="button"
              onClick={() => setAutoRefresh((v) => !v)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm border transition-all ${
                autoRefresh
                  ? 'bg-app-accent/15 border-app-accent/40 text-app-accent shadow-[0_0_0_3px_rgb(var(--color-app-accent)/0.1)]'
                  : 'border-app-border text-app-text-sec hover:text-app-text'
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  autoRefresh ? 'bg-app-accent animate-pulse' : 'bg-app-text-dim'
                }`}
              />
              Auto {autoRefresh ? 'ON' : 'OFF'}
            </button>
          </div>
        </div>
      </div>

      {hasAnyError && (
        <div className="shrink-0 rounded-lg border border-app-error/20 bg-app-error/10 px-3 py-2 text-xs text-app-error">
          {[latest.error, daily.error, periodic.error].filter(Boolean).join(' · ')}
        </div>
      )}

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-2 overflow-hidden lg:grid-cols-12 lg:grid-rows-[minmax(0,1fr)_minmax(0,5.75rem)]">
        <Panel
          title="Memory graph"
          icon={SparklesIcon}
          className="lg:col-span-10 lg:row-start-1 min-h-[280px] lg:min-h-0 border-app-accent/30 shadow-md"
          bodyClassName="!p-0 flex flex-col min-h-0"
        >
          <div className="flex-1 min-h-0 p-1.5">
            {daily.data?.items?.length ? (
              <MemoryGraph
                dailyItems={daily.data.items}
                periodicItems={periodic.data?.items ?? []}
                filterOrg={orgId}
                onNodeClick={setSelectedGraphNode}
              />
            ) : (
              <div className="flex h-full min-h-[240px] items-center justify-center">
                <EmptyState text="No data for graph." icon={SparklesIcon} />
              </div>
            )}
          </div>
          {selectedGraphNode?.type === 'daily' && selectedGraphNode.data && (
            <div className="shrink-0 border-t border-app-border/60 bg-app-muted/20 px-3 py-2 text-[11px] text-app-text-sec max-h-20 overflow-y-auto">
              <button
                type="button"
                onClick={() => setSelectedGraphNode(null)}
                className="mb-1 text-app-text-dim hover:text-app-accent"
              >
                Clear selection · {selectedGraphNode.label}
              </button>
              <p className="line-clamp-2">
                {(selectedGraphNode.data as DailySummaryItem).summary_md.slice(0, 280)}
                {(selectedGraphNode.data as DailySummaryItem).summary_md.length > 280 ? '…' : ''}
              </p>
            </div>
          )}
        </Panel>

        <div className="flex min-h-0 flex-col gap-2 overflow-hidden lg:col-span-2 lg:row-start-1">
          <Panel
            title="Activity (30d)"
            icon={CalendarDaysIcon}
            className="shrink-0 max-h-[7.5rem]"
            bodyClassName="!py-2"
          >
            {daily.data?.items?.length ? (
              <DailySummaryChart items={daily.data.items} />
            ) : (
              <p className="text-[10px] text-app-text-dim">No activity</p>
            )}
          </Panel>

          <Panel title="Latest daily" icon={DocumentTextIcon} className="min-h-0 flex-1">
            {latest.loading && <SkeletonCard />}
            {latest.data?.daily ? (
              <div className="space-y-1 text-[11px]">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-app-text">{latest.data.daily.summary_date}</span>
                  <StatusBadge variant="info" className="!px-1.5 !py-0 !text-[9px]">
                    {latest.data.daily.source_turn_count}
                  </StatusBadge>
                </div>
                <p className="text-app-text-sec line-clamp-5">{latest.data.daily.summary_md}</p>
              </div>
            ) : !latest.loading ? (
              <p className="text-[10px] text-app-text-dim">—</p>
            ) : null}
          </Panel>

          <Panel title="Periodic" icon={ClockIcon} className="shrink-0">
            {periodic.data?.items?.length ? (
              <PeriodDonut items={periodic.data.items} />
            ) : (
              <p className="text-[10px] text-app-text-dim">—</p>
            )}
          </Panel>
        </div>

        <Panel
          title={`Recent daily (${recentDailyItems.length})`}
          icon={CalendarDaysIcon}
          className="lg:col-span-6 lg:row-start-2 max-h-[5.75rem]"
          bodyClassName="!py-1.5"
        >
          {recentDailyItems.length ? (
            <div className="flex gap-2 overflow-x-auto pb-0.5">
              {recentDailyItems.map((item) => (
                <div
                  key={`${item.summary_date}-${item.org_id}-${item.station_id}`}
                  className="shrink-0 w-40 rounded-md border border-app-border/60 px-2 py-1 text-[10px]"
                  title={item.summary_md}
                >
                  <p className="font-medium text-app-text truncate">{item.summary_date}</p>
                  <p className="text-app-text-dim tabular-nums">{item.source_turn_count} turns</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[10px] text-app-text-dim">—</p>
          )}
        </Panel>

        <Panel
          title={`Recent periodic (${recentPeriodicItems.length})`}
          icon={ClockIcon}
          className="lg:col-span-6 lg:row-start-2 max-h-[5.75rem]"
          bodyClassName="!py-1.5"
        >
          {recentPeriodicItems.length ? (
            <div className="flex gap-2 overflow-x-auto pb-0.5">
              {recentPeriodicItems.map((item) => (
                <div
                  key={`${item.period_type}-${item.period_start_date}-${item.org_id}-${item.station_id}`}
                  className="shrink-0 w-44 rounded-md border border-app-border/60 px-2 py-1 text-[10px]"
                  title={item.summary_md}
                >
                  <p className="font-medium text-app-text truncate capitalize">
                    {item.period_type} · {item.period_start_date}
                  </p>
                  <p className="text-app-text-dim tabular-nums">{item.source_turn_count} turns</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[10px] text-app-text-dim">—</p>
          )}
        </Panel>
      </div>

    </div>
  );
}
